(() => {
  'use strict';
  if (document.documentElement.hasAttribute('data-leslis-gpo-adapter')) return;
  document.documentElement.setAttribute('data-leslis-gpo-adapter', '');

  const observed = new WeakSet();
  let inFlight = false;
  let requested = false;
  let scheduled = false;
  const validationObserved = new WeakSet();
  const invalidControls = new WeakMap();
  const requiredControls = new WeakSet();
  const validationErrors = new WeakMap();
  let validationAttempt;

  function syncValidation(root) {
    const errors = [];
    root.querySelectorAll('.gpo-element').forEach((group) => {
      const controls = Array.from(group.querySelectorAll('input:not([type="hidden"]), textarea, select'));
      const message = group.querySelector('.messages .error');
      const visible = group.getClientRects().length && !group.closest('[hidden]');
      const invalid = visible && group.classList.contains('has-error') && message?.getClientRects().length && message.textContent.trim();
      controls.forEach((control) => {
        const required = control.hasAttribute('presence') || group.querySelector('.gpo-label.required');
        if (required && !control.hasAttribute('aria-required')) {
          control.setAttribute('aria-required', 'true');
          requiredControls.add(control);
        } else if (!required && requiredControls.has(control)) {
          if (control.getAttribute('aria-required') === 'true') control.removeAttribute('aria-required');
          requiredControls.delete(control);
        }
        const controlVisible = control.getClientRects().length && !control.closest('[hidden]') && getComputedStyle(control).visibility !== 'hidden';
        const previous = invalidControls.get(control);
        if (previous && (!invalid || !controlVisible || previous.message !== message)) {
          const ids = (control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
          if (previous.added) {
            const remaining = ids.filter((id) => id !== previous.message.id).join(' ');
            if (remaining) control.setAttribute('aria-describedby', remaining);
            else control.removeAttribute('aria-describedby');
          }
          if (control.getAttribute('aria-invalid') === 'true') {
            if (previous.invalid === null) control.removeAttribute('aria-invalid');
            else control.setAttribute('aria-invalid', previous.invalid);
          }
          invalidControls.delete(control);
        }
        if (!invalid || !controlVisible || control.disabled || control.type === 'hidden') return;
        if (required && !control.value.trim() && control.matches('input[type="text"], textarea')) {
          const field = group.querySelector('.label-content')?.textContent.trim().replace(/[:*]\s*$/, '') || control.name;
          const pattern = document.getElementById('LeslisGpoFeedback')?.dataset.requiredFieldMessage;
          const text = pattern?.replace('[field]', field);
          if (text && message.textContent !== text) message.textContent = text;
        }
        if (!message.id) message.id = `LeslisGpoError-${root.id}-${group.dataset.elementId || control.id}`;
        const ids = new Set((control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        if (!invalidControls.has(control)) {
          invalidControls.set(control, { message, added: !ids.has(message.id), invalid: control.getAttribute('aria-invalid') });
        }
        if (!ids.has(message.id)) {
          ids.add(message.id);
          control.setAttribute('aria-describedby', Array.from(ids).join(' '));
        }
        if (control.getAttribute('aria-invalid') !== 'true') control.setAttribute('aria-invalid', 'true');
        errors.push({ control, message });
      });
    });
    const alert = document.getElementById('LeslisGpoValidationAlert');
    const messages = errors.map(({ message }) => message.textContent.trim()).filter((text, index, all) => all.indexOf(text) === index).join(' ');
    const signature = errors.map(({ message }) => `${message.id}:${message.textContent.trim()}`).join('|');
    const previousErrors = validationErrors.get(root);
    validationErrors.set(root, signature);
    if (!errors.length && alert?.textContent) alert.textContent = '';
    else if (alert && previousErrors !== undefined && previousErrors !== signature) alert.textContent = messages;
    const attempt = validationAttempt;
    if (!attempt || attempt.root !== root || !root.isConnected) return;
    const current = document.activeElement;
    if (current !== attempt.trigger && current !== attempt.activeElement && current !== errors[0]?.control) return;
    if (!errors.length) return;
    validationAttempt = undefined;
    if (alert) alert.textContent = messages;
    errors[0].control.focus();
  }

  function watchValidation() {
    document.querySelectorAll('product-info[id^="MainProduct-"] .product-form').forEach((form) => {
      if (validationObserved.has(form)) return;
      validationObserved.add(form);
      const root = form.closest('product-info');
      new MutationObserver(() => syncValidation(root)).observe(form, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'presence'],
      });
      syncValidation(root);
    });
  }

  function armValidation(event) {
    if (event.type === 'keydown' && (!['Enter', ' '].includes(event.key) || event.repeat)) return;
    const trigger = event.type === 'submit' ? event.submitter : event.target.closest('button[type="submit"], .gpo-clone-atc-button');
    const root = trigger?.closest('product-info[id^="MainProduct-"]');
    if (!root?.querySelector('.gpo-element')) {
      validationAttempt = undefined;
      return;
    }
    const attempt = { root, trigger, activeElement: document.activeElement };
    validationAttempt = attempt;
    const alert = document.getElementById('LeslisGpoValidationAlert');
    if (alert) alert.textContent = '';
    queueMicrotask(() => syncValidation(root));
    setTimeout(() => { if (validationAttempt === attempt) validationAttempt = undefined; }, 2000);
  }

  window.addEventListener('click', armValidation, true);
  window.addEventListener('submit', armValidation, true);
  window.addEventListener('keydown', armValidation, true);
  window.addEventListener('focusin', (event) => {
    if (validationAttempt && event.target !== validationAttempt.trigger && event.target !== validationAttempt.activeElement) validationAttempt = undefined;
  }, true);

  function restoreConfigurationHint() {
    document.querySelectorAll('product-info [data-leslis-gpo-configuration-label]').forEach((price) => {
      const amount = price.textContent.trim();
      if (/0/.test(amount) && !/[1-9]/.test(amount)) {
        const label = price.dataset.leslisGpoConfigurationLabel;
        if (label && amount !== label) price.textContent = label;
      }
    });
  }

  function watchProductPrices() {
    document.querySelectorAll('product-info[id^="MainProduct-"] [id^="price-"]').forEach((container) => {
      if (observed.has(container)) return;
      observed.add(container);
      new MutationObserver(restoreConfigurationHint).observe(container, { childList: true, subtree: true, characterData: true });
    });
    restoreConfigurationHint();
    watchValidation();
  }

  function feedback(show) {
    let message = document.querySelector('[data-leslis-gpo-feedback]');
    if (!message && show) {
      const template = document.getElementById('LeslisGpoFeedback');
      const target = document.querySelector('product-info[id^="MainProduct-"] .product-form') ||
        document.querySelector('product-info[id^="MainProduct-"] .product__info-container');
      if (template && target) {
        target.append(template.content.cloneNode(true));
        message = target.querySelector('[data-leslis-gpo-feedback]');
      }
    }
    if (message) message.hidden = !show;
  }

  async function refreshCart() {
    scheduled = false;
    if (inFlight || !requested) return;
    requested = false;
    inFlight = true;
    const drawer = document.querySelector('cart-drawer');
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 8000);
    let stage = 'drawer';
    let responseStatus = 0;
    document.documentElement.dataset.leslisGpoRefresh = 'loading';
    try {
      if (!drawer || typeof drawer.getSectionsToRender !== 'function' || typeof drawer.renderContents !== 'function') {
        throw new Error('Cart drawer unavailable');
      }
      const definitions = drawer.getSectionsToRender();
      if (!definitions.length || definitions.length > 5) throw new Error('Invalid cart sections');
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', definitions.map((section) => section.id).join(','));
      drawer.setActiveElement?.(document.activeElement);
      stage = 'request';
      const response = await fetch(url.toString(), {
        method: 'GET', credentials: 'same-origin', signal: abort.signal,
      });
      responseStatus = response.status || 0;
      if (!response.ok) throw new Error('Cart refresh failed');
      stage = 'json';
      const sections = await response.json();
      for (const section of definitions) {
        stage = section.id === 'cart-drawer' ? 'drawer-section' : 'counter-section';
        const html = sections?.[section.id];
        if (typeof html !== 'string' || !new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section')) {
          throw new Error('Missing cart section');
        }
      }
      if (!requested) {
        stage = 'render';
        drawer.classList.remove('is-empty');
        drawer.renderContents({ sections });
        feedback(false);
        document.documentElement.dataset.leslisGpoRefresh = 'rendered';
      }
    } catch {
      if (!requested) {
        feedback(true);
        const reason = abort.signal.aborted ? 'timeout' : stage;
        document.documentElement.dataset.leslisGpoRefresh = reason;
        console.warn('[Leslis GPO] Cart display refresh failed:', reason, responseStatus);
      }
    } finally {
      clearTimeout(timeout);
      inFlight = false;
      if (requested) scheduleRefresh();
    }
  }

  function scheduleRefresh() {
    if (inFlight || scheduled) return;
    scheduled = true;
    queueMicrotask(refreshCart);
  }

  document.addEventListener('gpoAddedToCart', () => {
    validationAttempt = undefined;
    requested = true;
    scheduleRefresh();
  });
  document.addEventListener('gpoProductPriceUpdated', restoreConfigurationHint);
  document.addEventListener('gpoRenderCompleted', watchProductPrices);
  document.addEventListener('shopify:section:load', watchProductPrices);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchProductPrices, { once: true });
  else watchProductPrices();
})();
